
import React, { useState, useEffect, useCallback } from 'react';
import styles from './HeaderTable.module.css';
import * as XLSX from 'xlsx';
import { orderHeaderApi } from '../../../../../api/orderHeader/orderHeaderApi';

const HeaderTable = ({ onOrderClick, savedState }) => {
  const [orders, setOrders] = useState([]);
  const [currentPage, setCurrentPage] = useState(savedState?.currentPage || 0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [searchTerm, setSearchTerm] = useState(savedState?.searchTerm || '');
  const [selectedYear, setSelectedYear] = useState(savedState?.selectedYear || '');
  const [selectedMonth, setSelectedMonth] = useState(savedState?.selectedMonth || '');
  // ✅ CHANGE THIS LINE - set default to empty string instead of 'Open'
  const [selectedStatus, setSelectedStatus] = useState( '');
  const [sortBy, setSortBy] = useState(savedState?.sortBy || '');
  const [sortOrder, setSortOrder] = useState(savedState?.sortOrder || 'desc');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [availableYears, setAvailableYears] = useState([]);
  const itemsPerPage = 20;

  const months = [
    { label: 'All Months', value: '' },
    { label: 'January', value: '1' },
    { label: 'February', value: '2' },
    { label: 'March', value: '3' },
    { label: 'April', value: '4' },
    { label: 'May', value: '5' },
    { label: 'June', value: '6' },
    { label: 'July', value: '7' },
    { label: 'August', value: '8' },
    { label: 'September', value: '9' },
    { label: 'October', value: '10' },
    { label: 'November', value: '11' },
    { label: 'December', value: '12' }
  ];

  const statusFilters = [
    { label: 'All', value: '' },
    { label: 'Open', value: 'Open' },
    { label: 'Closed', value: 'Closed' },
    { label: 'Partial', value: 'Partial' }
  ];

  // Fetch available years from the API (first order date to current year)
  useEffect(() => {
    const fetchYears = async () => {
      try {
        const data = await orderHeaderApi.getOrderYears();
        if (data && Array.isArray(data)) {
          setAvailableYears(data);
        }
      } catch (err) {
        // Fallback: build years from current year back to 2023
        const currentYear = new Date().getFullYear();
        const years = [];
        for (let y = currentYear; y >= 2023; y--) {
          years.push(y.toString());
        }
        setAvailableYears(years);
      }
    };
    fetchYears();
  }, []);

  const getCurrentTableState = () => ({
    currentPage,
    searchTerm,
    selectedYear,
    selectedMonth,
    selectedStatus,
    sortBy,
    sortOrder
  });

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await orderHeaderApi.getOrderHeaders(
        currentPage,
        itemsPerPage,
        searchTerm,
        selectedMonth,
        selectedStatus,
        sortBy,
        sortOrder,
        selectedYear
      );

      if (data && data.orderHeaders && Array.isArray(data.orderHeaders)) {
        setOrders(data.orderHeaders);
        setTotalPages(data.totalPages || 0);
        setTotalItems(data.totalItems || 0);
      } else {
        setOrders([]);
        setError('Invalid data received from server');
      }
    } catch (err) {
      setError('Failed to load order headers');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, selectedMonth, selectedStatus, sortBy, sortOrder, selectedYear]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedYear('');
    setSelectedMonth('');
    setSelectedStatus(''); // ✅ Clear to empty string (All)
    setSortBy('');
    setSortOrder('desc');
    setCurrentPage(0);
  };

  const handleYearChange = (value) => {
    setSelectedYear(value);
    setCurrentPage(0);
  };

  const handleMonthChange = (value) => {
    setSelectedMonth(value);
    setCurrentPage(0);
  };

  const handleStatusChange = (value) => {
    setSelectedStatus(value);
    setCurrentPage(0);
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
    setCurrentPage(0);
  };

  const handleOrderClick = (orderNum) => {
    if (onOrderClick) {
      onOrderClick(orderNum, getCurrentTableState());
    }
  };

  const handleExportExcel = async () => {
    try {
      setLoading(true);
      const data = await orderHeaderApi.exportOrderHeaders(searchTerm, selectedMonth, selectedStatus, selectedYear);

      if (!data || !Array.isArray(data) || data.length === 0) {
        alert('No data to export');
        return;
      }

      const worksheet = XLSX.utils.json_to_sheet(data.map(order => ({
        'Order#': order.orderNum,
        'Order Status': order.orderStatus,
        'Customer PO No': order.customerPoNo,
        'Order Date': order.orderDate,
        'Order Time': order.orderTime,
        'Customer Name': order.customerCode,
        'Contact Person': order.contactPerson,
        'Delivery Date': order.deliveryDate,
        'Sales Employee Name': order.salesEmployeeName,
        'Subtotal': order.subtotal,
        'Tax Total': order.taxTotal,
        'Total Amount': order.totalAmount,
        'Payment Terms': order.paymentTerms,
      })));

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Order Headers');
      XLSX.writeFile(workbook, `order_headers_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (err) {
      alert('Failed to export data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return '₹' + (amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatTime = (timeValue) => {
    if (!timeValue) return 'N/A';
    try {
      const date = new Date(timeValue);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return 'N/A';
    }
  };

  const getSortIcon = (column) => {
    if (sortBy !== column) return '⇅';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  const hasActiveFilters = searchTerm || selectedYear || selectedMonth || selectedStatus;

  return (
    <div className={styles.wrapper}>
      <div className={styles.filtersSection}>
        {/* Search Row - no button */}
        <div className={styles.searchRow}>
          <div className={styles.searchInputWrapper}>
            <span className={styles.searchIcon}>🔍</span>
            <input
              type="text"
              placeholder="Search by Order#, Customer PO#, Customer, Contact Person..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={styles.searchInput}
            />
            {searchTerm && (
              <button onClick={() => setSearchTerm('')} className={styles.clearSearchButton} title="Clear search">
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Filter Row */}
        <div className={styles.filterRow}>
          <div className={styles.filterGroupRow}>
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Year:</label>
              <select
                value={selectedYear}
                onChange={(e) => handleYearChange(e.target.value)}
                className={styles.filterSelect}
                disabled={loading}
              >
                <option value="">All Years</option>
                {availableYears.map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>

            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Month:</label>
              <select
                value={selectedMonth}
                onChange={(e) => handleMonthChange(e.target.value)}
                className={styles.filterSelect}
                disabled={loading}
              >
                {months.map((month) => (
                  <option key={month.value} value={month.value}>{month.label}</option>
                ))}
              </select>
            </div>

            <div className={styles.statusButtonGroup}>
              {statusFilters.map((filter) => (
                <button
                  key={filter.value}
                  className={`${styles.statusButton} ${selectedStatus === filter.value ? styles.statusButtonActive : ''}`}
                  onClick={() => handleStatusChange(filter.value)}
                  disabled={loading}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>

          <div className={styles.actionButtons}>
            <button onClick={handleClearFilters} disabled={loading} className={styles.clearButton}>
              Clear All Filters
            </button>
            <button onClick={handleExportExcel} disabled={loading || totalItems === 0} className={styles.excelButton}>
              <span className={styles.excelIcon}>📊</span>
              Export Excel
            </button>
          </div>
        </div>
      </div>

      {hasActiveFilters && (
        <div className={styles.activeFilters}>
          <span className={styles.activeFiltersLabel}>Active Filters:</span>
          {searchTerm && (
            <div className={styles.filterTag}>
              Search: "{searchTerm}"
              <button onClick={() => setSearchTerm('')} className={styles.removeFilterButton}>✕</button>
            </div>
          )}
          {selectedYear && (
            <div className={styles.filterTag}>
              Year: {selectedYear}
              <button onClick={() => handleYearChange('')} className={styles.removeFilterButton}>✕</button>
            </div>
          )}
          {selectedMonth && (
            <div className={styles.filterTag}>
              Month: {months.find(m => m.value === selectedMonth)?.label}
              <button onClick={() => handleMonthChange('')} className={styles.removeFilterButton}>✕</button>
            </div>
          )}
          {selectedStatus && (
            <div className={styles.filterTag}>
              Status: {selectedStatus}
              <button onClick={() => handleStatusChange('')} className={styles.removeFilterButton}>✕</button>
            </div>
          )}
        </div>
      )}

      {error && <div className={styles.errorMessage}>{error}</div>}

      {loading && (
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner} />
          <div className={styles.loadingText}>Loading order headers...</div>
        </div>
      )}

      {!loading && (
        <div className={styles.tableContainer}>
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.th} onClick={() => handleSort('orderNum')}>
                    Order# {getSortIcon('orderNum')}
                  </th>
                  <th className={styles.th} onClick={() => handleSort('orderStatus')}>
                    Status {getSortIcon('orderStatus')}
                  </th>
                  <th className={styles.th}>Customer PO No</th>
                  <th className={styles.th} onClick={() => handleSort('orderDate')}>
                    Order Date {getSortIcon('orderDate')}
                  </th>
                  <th className={styles.th}>Order Time</th>
                  <th className={styles.th}>Customer Name</th>
                  <th className={styles.th}>Contact Person</th>
                  <th className={styles.th}>Delivery Date</th>
                  <th className={styles.th}>Sales Employee</th>
                  <th className={styles.th} onClick={() => handleSort('subtotal')}>
                    Subtotal {getSortIcon('subtotal')}
                  </th>
                  <th className={styles.th}>Tax Total</th>
                  <th className={styles.th} onClick={() => handleSort('totalAmount')}>
                    Total Amount {getSortIcon('totalAmount')}
                  </th>
                  <th className={styles.th}>Payment Terms</th>
                </tr>
              </thead>
              <tbody>
                {orders.length > 0 ? (
                  orders.map((order, index) => (
                    <tr key={`${order.orderNum}-${index}`} className={styles.tr}>
                      <td className={styles.td}>
                        <span
                          onClick={() => handleOrderClick(order.orderNum)}
                          className={styles.clickableCell}
                        >
                          {order.orderNum}
                        </span>
                      </td>
                      <td className={styles.td}>
                        <span className={`${styles.statusBadge} ${styles[`status${order.orderStatus}`]}`}>
                          {order.orderStatus}
                        </span>
                      </td>
                      <td className={styles.td}>
                        {order.customerPoNo ? (
                          <span
                            onClick={() => handleOrderClick(order.orderNum)}
                            className={styles.clickableCell}
                          >
                            {order.customerPoNo}
                          </span>
                        ) : 'N/A'}
                      </td>
                      <td className={styles.td}>{formatDate(order.orderDate)}</td>
                      <td className={styles.td}>{formatTime(order.orderTime)}</td>
                      <td className={styles.td}>{order.customerCode}</td>
                      <td className={styles.td}>{order.contactPerson || 'N/A'}</td>
                      <td className={styles.td}>{formatDate(order.deliveryDate)}</td>
                      <td className={styles.td}>{order.salesEmployeeName || 'N/A'}</td>
                      <td className={styles.td}>{formatCurrency(order.subtotal)}</td>
                      <td className={styles.td}>{formatCurrency(order.taxTotal)}</td>
                      <td className={styles.td}>
                        <span className={styles.totalAmount}>{formatCurrency(order.totalAmount)}</span>
                      </td>
                      <td className={styles.td}>{order.paymentTerms || 'N/A'}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td className={styles.td} colSpan="13" style={{ textAlign: 'center', padding: '40px' }}>
                      {hasActiveFilters ? 'No orders match your filters' : 'No orders found'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && orders.length > 0 && (
        <div className={styles.paginationSection}>
          <div className={styles.paginationInfo}>
            Showing {currentPage * itemsPerPage + 1} - {Math.min((currentPage + 1) * itemsPerPage, totalItems)} of {totalItems} records
          </div>
          <div className={styles.paginationControls}>
            <button onClick={() => setCurrentPage(0)} disabled={currentPage === 0} className={styles.paginationButton}>First</button>
            <button onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))} disabled={currentPage === 0} className={styles.paginationButton}>Prev</button>
            <div className={styles.pageNumber}>{currentPage + 1} / {totalPages}</div>
            <button onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))} disabled={currentPage === totalPages - 1} className={styles.paginationButton}>Next</button>
            <button onClick={() => setCurrentPage(totalPages - 1)} disabled={currentPage === totalPages - 1} className={styles.paginationButton}>Last</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default HeaderTable;